import {DataTableActionHeader, DataTableCard, TablePending} from "../DataTable/DataTable"
import {useAccessToken, useIsUnitUser, useUserType} from "../../services/auth"
import {AsyncResultComponent} from "../../containers/AsyncResult/AsyncResult"
import React, {useEffect, useMemo} from "react"
import {useQueryState} from "use-location-state"
import {usePaging} from "../../hooks/usePaging"
import {
	findIncomingRealTimePayments,
	IncomingRealTimePaymentStatus,
	isIncomingRealTimePayment,
} from "../../resources/incomingRealTimePayment"
import {useRefreshToken} from "../../hooks/useRefreshToken"
import {Filter} from "../Filter/Filter"
import {startCase} from "lodash"
import {ErrorDocument} from "../../resources/common"
import moment from "moment"
import {findOrgs, Org} from "../../resources/org"
import {useAsyncResult} from "../../hooks/useAsyncResult"
import {AsyncResult} from "../../types/asyncResult"
import {Cents} from "../../resources/transaction"
import {CurrencyRangeFilter} from "../Filter/CurrencyRangeFilter"
import DatePickerWithPresets, {DatePickerPresetKeys} from "../DatePicker/DatePicker"
import {Bank, findBanks} from "../../resources/bank"
import {OrgFilter} from "../OrgFilter/OrgFilter"
import {AllowedIncomingRtpColumns} from "./IncomingRtp.types"
import {IncomingRtpTable} from "./IncomingRtpTable"

interface IncomingRtpProps {
	limit?: number
	includedColumns: AllowedIncomingRtpColumns[]
	enableStatusFilter?: boolean
	enableOrgFilter?: boolean
	enableBankFilter?: boolean
	enableAmountFilter?: boolean
	title?: string
	fullHeight?: boolean
	stickyAction?: boolean
	enablePaging?: boolean
}

export default function IncomingRtp({
	limit = 25,
	includedColumns,
	stickyAction = false,
	fullHeight = false,
	enablePaging = true,
	enableStatusFilter = true,
	enableOrgFilter = true,
	enableAmountFilter = true,
	enableBankFilter = true,
	title,
}: IncomingRtpProps) {
	const prefix = "inc-rtp"
	const accessToken = useAccessToken()
	const [refreshToken, refresh] = useRefreshToken()
	const userType = useUserType()

	const [statuses, setStatuses] = useQueryState<IncomingRealTimePaymentStatus[]>(`${prefix}-filter[status]`, [])
	const statusesOptions = new Map(Object.values(IncomingRealTimePaymentStatus).map((v) => [v, startCase(v)]))
	const statusFilter = (
		<Filter
			statuses={statuses}
			setStatuses={setStatuses}
			onFilterFunc={() => reset(limit)}
			options={statusesOptions}
			title="Status"
		/>
	)

	const [filteredOrgs, setFilteredOrgs] = useQueryState<string[]>(`${prefix}-filter[orgId]`, [])
	const [filteredBanks, setFilteredBanks] = useQueryState<string[]>(`${prefix}-filter[banks]`, [])
	const [fromAmount, setFromAmount] = useQueryState<Cents | "">(`${prefix}-filter[fromAmount]`, "")
	const [toAmount, setToAmount] = useQueryState<Cents | "">(`${prefix}-filter[toAmount]`, "")
	const [since, setSince] = useQueryState(`${prefix}-filter[since]`, "")
	const [until, setUntil] = useQueryState(`${prefix}-filter[until]`, "")

	const [result, hasPrev, hasNext, prev, next, hasResults, reset] = usePaging(
		limit,
		(offset, limit) =>
			findIncomingRealTimePayments(
				accessToken,
				offset,
				limit,
				statuses,
				filteredOrgs,
				filteredBanks,
				fromAmount,
				toAmount,
				since,
				until
			),
		(x) => x.data.length,
		[refreshToken, [...statuses, ...filteredOrgs, ...filteredBanks].join(","), fromAmount, toAmount, since, until],
		`${prefix}-`,
		true
	)

	useEffect(() => {
		result.match(
			() => null,
			() => null,
			() => null
		)
	}, [result])

	const orgs =
		userType === "unit" || userType === "bank"
			? useAsyncResult(() => findOrgs(accessToken, 0, 10000), [])
			: AsyncResult.pending<Org[], ErrorDocument>()

	const orgFilter = useMemo(
		() => OrgFilter(orgs, setFilteredOrgs, filteredOrgs, reset, limit),
		[orgs, setFilteredOrgs, filteredOrgs.join(","), reset, limit]
	)

	const banks = useIsUnitUser()
		? useAsyncResult(() => findBanks(accessToken, 0, 10000), [])
		: AsyncResult.pending<Bank[], ErrorDocument>()
	const bankFilter = banks.match(
		() => null,
		(banks) => (
			<Filter
				title="Banks"
				isSearchable={true}
				setStatuses={setFilteredBanks}
				statuses={filteredBanks}
				onFilterFunc={() => reset(limit)}
				options={
					new Map<string, string>(
						banks
							.sort((a, b) => moment(b.attributes.createdAt).diff(moment(a.attributes.createdAt)))
							.map((bank) => [bank.id, bank.attributes.name])
					)
				}
			/>
		),
		(_) => null
	)

	const amountFilter = (
		<CurrencyRangeFilter
			placeholder={"Amount"}
			presets={[]}
			from={fromAmount}
			setFrom={setFromAmount}
			to={toAmount}
			setTo={setToAmount}
		/>
	)

	const dateTime = (
		<DatePickerWithPresets
			calendarPosition={"right"}
			presets={[
				DatePickerPresetKeys.allTime,
				DatePickerPresetKeys.yesterday,
				DatePickerPresetKeys.lastMonth,
				DatePickerPresetKeys.last3Months,
				DatePickerPresetKeys.last6Months,
				DatePickerPresetKeys.last30Days,
				DatePickerPresetKeys.last7Days,
				DatePickerPresetKeys.custom,
			]}
			onDateChanged={(s, u) => {
				setSince(s)
				setUntil(u)
			}}
		/>
	)

	return (
		<DataTableCard className={"incoming-rtp-card"}>
			<DataTableActionHeader
				enableSticky={fullHeight}
				dateTimePicker={dateTime}
				filters={[
					...(enableOrgFilter ? [orgFilter] : []),
					...(enableBankFilter ? [bankFilter] : []),
					...(enableStatusFilter ? [statusFilter] : []),
					...(enableAmountFilter ? [amountFilter] : []),
				]}
				title={title}
			/>
			<AsyncResultComponent
				asyncResult={result}
				pendingComponent={<TablePending numberOfRows={includedColumns.length} />}
			>
				{({value: incomingRtps}) => {
					return (
						<IncomingRtpTable
							incomingRtps={incomingRtps.data.filter(isIncomingRealTimePayment)}
							hasResults={hasResults}
							hasPrev={hasPrev}
							hasNext={hasNext}
							prev={prev}
							next={next}
							refresh={refresh}
							include={incomingRtps.included}
							includedColumns={includedColumns}
							isUsingPaging={enablePaging}
							stickyAction={stickyAction}
						/>
					)
				}}
			</AsyncResultComponent>
		</DataTableCard>
	)
}
